import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  DatePicker,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { authFetch } from '../../utils/api';
import { exportCsv } from '../../utils/csv';
import { BrandScopeNotice } from '../../components/BrandScopeNotice';
import { useBrandOptions } from '../../hooks/useBrandOptions';
import { getBrandLabel } from '../../utils/tokenManager';

const { Title } = Typography;
const { RangePicker } = DatePicker;

interface WorkerLogRecord {
  id: number;
  workerName: string;
  eventType: string;
  level: string;
  message: string;
  clientIp: string | null;
  colo: string | null;
  outcome: string | null;
  requestUrl: string | null;
  method: string | null;
  eventTimestamp: number | null;
  /** Sprint 19 W5-D (E2#4) — derived brand attribution (NULL = legacy / cross-brand). */
  brand: string | null;
  createdAt: string;
}

interface WorkerLogStats {
  workerName: string;
  eventType: string;
  count: number;
}

const levelColors: Record<string, string> = {
  error: 'red',
  warn: 'orange',
  info: 'blue',
  log: 'default',
};

const eventColors: Record<string, string> = {
  error: 'red',
  exception: 'red',
  auth_error: 'orange',
  quota_exceeded: 'orange',
  token_expired: 'orange',
  outcome_error: 'volcano',
  info: 'blue',
};

export const WorkerLogList: React.FC = () => {
  const [data, setData] = useState<WorkerLogRecord[]>([]);
  const [stats, setStats] = useState<WorkerLogStats[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [workerName, setWorkerName] = useState('');
  const [eventType, setEventType] = useState('');
  const [level, setLevel] = useState('');
  const [brand, setBrand] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    null,
  );
  const brandOptions = useBrandOptions();

  const fetchStats = useCallback(async () => {
    try {
      const res = await authFetch('/api/admin/worker-logs/stats?hours=24');
      const result = await res.json();
      if (result.code === 0) {
        setStats(Array.isArray(result.data) ? result.data : []);
      }
    } catch {
      // stats are informational only
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      if (workerName) params.set('workerName', workerName);
      if (eventType) params.set('eventType', eventType);
      if (level) params.set('level', level);
      if (brand) params.set('brand', brand);
      if (dateRange) {
        params.set('startTime', dateRange[0].format('YYYY-MM-DD HH:mm:ss'));
        params.set('endTime', dateRange[1].format('YYYY-MM-DD HH:mm:ss'));
      }

      const res = await authFetch(`/api/admin/worker-logs?${params.toString()}`);
      const result = await res.json();
      if (result.code === 0 && result.data) {
        setData(Array.isArray(result.data.list) ? result.data.list : []);
        setTotal(result.data.total ?? 0);
      }
    } finally {
      setLoading(false);
    }
  }, [brand, dateRange, eventType, level, page, pageSize, workerName]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExportCsv = () => {
    exportCsv(
      data,
      [
        { key: 'id', title: 'ID' },
        { key: 'workerName', title: 'Worker' },
        { key: 'brand', title: '品牌' },
        { key: 'eventType', title: '事件' },
        { key: 'level', title: '等級' },
        { key: 'message', title: '訊息' },
        { key: 'clientIp', title: '客戶 IP' },
        { key: 'colo', title: 'Colo' },
        { key: 'outcome', title: 'Outcome' },
        { key: 'method', title: 'HTTP 方法' },
        { key: 'requestUrl', title: '請求 URL' },
        { key: 'createdAt', title: '時間' },
      ],
      'worker-logs-export',
    );
  };

  return (
    <div className="page-enter">
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          gap: 12,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Worker 日誌
        </Title>
        <Button icon={<DownloadOutlined />} onClick={handleExportCsv}>
          匯出 CSV
        </Button>
      </div>

      <BrandScopeNotice kind="logs" style={{ marginBottom: 12 }} />

      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          placeholder="Worker 名稱"
          allowClear
          onSearch={(value) => {
            setPage(1);
            setWorkerName(value.trim());
          }}
          style={{ width: 220, maxWidth: '100%' }}
        />
        <Select
          placeholder="事件"
          allowClear
          style={{ width: 170 }}
          onChange={(value) => {
            setPage(1);
            setEventType(value ?? '');
          }}
          options={[
            { label: 'info', value: 'info' },
            { label: 'error', value: 'error' },
            { label: 'auth_error', value: 'auth_error' },
            { label: 'quota_exceeded', value: 'quota_exceeded' },
            { label: 'token_expired', value: 'token_expired' },
            { label: 'exception', value: 'exception' },
            { label: 'outcome_error', value: 'outcome_error' },
          ]}
        />
        <Select
          placeholder="等級"
          allowClear
          style={{ width: 130 }}
          onChange={(value) => {
            setPage(1);
            setLevel(value ?? '');
          }}
          options={[
            { label: 'log', value: 'log' },
            { label: 'info', value: 'info' },
            { label: 'warn', value: 'warn' },
            { label: 'error', value: 'error' },
          ]}
        />
        <Select
          placeholder="品牌"
          allowClear
          style={{ width: 150 }}
          value={brand || undefined}
          onChange={(value) => {
            setPage(1);
            setBrand(value ?? '');
          }}
          options={brandOptions.map((option) => ({
            label: option.label,
            value: option.value,
          }))}
        />
        <RangePicker
          showTime
          onChange={(dates) => {
            setPage(1);
            setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null);
          }}
        />
      </Space>

      {stats.length > 0 ? (
        <Space wrap style={{ marginBottom: 12 }}>
          {stats.slice(0, 8).map((item) => (
            <Tag key={`${item.workerName}-${item.eventType}`}>
              {item.workerName} / {item.eventType}: {item.count}
            </Tag>
          ))}
        </Space>
      ) : null}

      <Table
        dataSource={data}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1200 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (count) => `共 ${count} 筆`,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage);
            setPageSize(nextPageSize);
          },
        }}
      >
        <Table.Column dataIndex="id" title="ID" width={80} />
        <Table.Column dataIndex="workerName" title="Worker" width={160} />
        <Table.Column
          dataIndex="brand"
          title="品牌"
          width={110}
          render={(value: string | null) =>
            value ? <Tag color="geekblue">{getBrandLabel(value)}</Tag> : <Tag>—</Tag>
          }
        />
        <Table.Column
          dataIndex="eventType"
          title="事件"
          width={150}
          render={(value: string) => (
            <Tag color={eventColors[value] || 'default'}>{value}</Tag>
          )}
        />
        <Table.Column
          dataIndex="level"
          title="等級"
          width={90}
          render={(value: string) => (
            <Tag color={levelColors[value] || 'default'}>{value}</Tag>
          )}
        />
        <Table.Column
          dataIndex="message"
          title="訊息"
          width={260}
          ellipsis
          render={(value: string) => (
            <Tooltip title={value}>
              <span>{value}</span>
            </Tooltip>
          )}
        />
        <Table.Column dataIndex="clientIp" title="客戶 IP" width={140} />
        <Table.Column dataIndex="colo" title="Colo" width={90} />
        <Table.Column dataIndex="outcome" title="Outcome" width={130} />
        <Table.Column dataIndex="method" title="方法" width={90} />
        <Table.Column
          dataIndex="requestUrl"
          title="請求 URL"
          width={260}
          ellipsis
          render={(value: string | null) => value || '-'}
        />
        <Table.Column
          dataIndex="createdAt"
          title="時間"
          width={180}
          render={(value: string) =>
            value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-'
          }
        />
      </Table>
    </div>
  );
};
